import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { generalApi } from '../../api/generalApi';
import { referenceAPI } from '../../api/api';
import DataTable from '../../components/ui/DataTable';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import FilterPanel from '../../components/ui/FilterPanel';
import Loading from '../../components/ui/Loading';
import { XMarkIcon } from '@heroicons/react/24/outline';

const STATUS_LABELS = {
  active: 'Aktif',
  expired: 'Süresi Dolmuş',
  pending: 'Beklemede',
  cancelled: 'İptal',
};

const LicenseListPage = () => {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const [licenses, setLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    vendor: '',
    product_name: '',
    license_key: '',
    license_type: '',
    hotel: '',
    department: '',
    start_date: '',
    expiry_date: '',
    renewal_date: '',
    cost: '',
    currency: 'TRY',
    notes: '',
    status: 'active',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [remindLoading, setRemindLoading] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);

  const loadData = async () => {
    try {
      const [licRes, typesRes, hotelsRes] = await Promise.all([
        generalApi.getLicenses(filters),
        generalApi.getLicenseTypes(),
        referenceAPI.getHotels(),
      ]);
      const licData = Array.isArray(licRes) ? licRes : (licRes?.data || licRes?.results || []);
      setLicenses(licData);
      const typesData = Array.isArray(typesRes) ? typesRes : (typesRes?.data || typesRes?.results || []);
      setLicenseTypes(typesData);
      const hotelsData = Array.isArray(hotelsRes) ? hotelsRes : (hotelsRes?.data || hotelsRes?.results || []);
      setHotels(hotelsData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async (hotelId) => {
    try {
      const res = await referenceAPI.getDepartments(hotelId);
      const data = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setDepartments(data);
    } catch (e) {
      console.error(e);
      setDepartments([]);
    }
  };

  useEffect(() => {
    loadData();
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    loadDepartments(formData.hotel || filters.hotel);
  }, [formData.hotel, filters.hotel]);

  const openCreate = () => {
    setEditingLicense(null);
    setSelectedLicense(null);
    setFormData({
      name: '',
      vendor: '',
      product_name: '',
      license_key: '',
      license_type: '',
      hotel: '',
      department: '',
      start_date: '',
      expiry_date: '',
      renewal_date: '',
      cost: '',
      currency: 'TRY',
      notes: '',
      status: 'active',
    });
    setModalOpen(true);
  };

  const openEdit = async (lic) => {
    setEditingLicense(lic);
    try {
      const full = await generalApi.getLicense(lic.id);
      const data = full?.data || full;
      setFormData({
        name: data?.name || lic.name || '',
        vendor: data?.vendor || lic.vendor || '',
        product_name: data?.product_name || lic.product_name || '',
        license_key: data?.license_key || '',
        license_type: data?.license_type ?? lic.license_type ?? '',
        hotel: data?.hotel ?? lic.hotel ?? '',
        department: data?.department ?? lic.department ?? '',
        start_date: data?.start_date || lic.start_date || '',
        expiry_date: data?.expiry_date || lic.expiry_date || '',
        renewal_date: data?.renewal_date || lic.renewal_date || '',
        cost: data?.cost ?? lic.cost ?? '',
        currency: data?.currency || lic.currency || 'TRY',
        notes: data?.notes || lic.notes || '',
        status: data?.status || lic.status || 'active',
      });
    } catch {
      setFormData({
        name: lic.name || '',
        vendor: lic.vendor || '',
        product_name: lic.product_name || '',
        license_key: '',
        license_type: lic.license_type || '',
        hotel: lic.hotel || '',
        department: lic.department || '',
        start_date: lic.start_date || '',
        expiry_date: lic.expiry_date || '',
        renewal_date: lic.renewal_date || '',
        cost: lic.cost ?? '',
        currency: lic.currency || 'TRY',
        notes: lic.notes || '',
        status: lic.status || 'active',
      });
    }
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.license_type) {
      alert('Lisans tipi zorunludur. Önce Tanımlamalar sayfasından lisans tipi ekleyin.');
      return;
    }
    setSaveLoading(true);
    try {
      const payload = {
        name: formData.name,
        vendor: formData.vendor || null,
        product_name: formData.product_name || null,
        license_key: formData.license_key || null,
        license_type: formData.license_type || null,
        hotel: formData.hotel || null,
        department: formData.department || null,
        start_date: formData.start_date || null,
        expiry_date: formData.expiry_date || null,
        renewal_date: formData.renewal_date || null,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        currency: formData.currency || 'TRY',
        notes: formData.notes || null,
        status: formData.status || 'active',
      };
      if (editingLicense) {
        await generalApi.updateLicense(editingLicense.id, payload);
      } else {
        await generalApi.createLicense(payload);
      }
      setModalOpen(false);
      setSelectedLicense(null);
      loadData();
    } catch (err) {
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (lic) => {
    if (!window.confirm(`"${lic.name}" lisansını silmek istediğinize emin misiniz?`)) return;
    try {
      await generalApi.deleteLicense(lic.id);
      setSelectedLicense(null);
      loadData();
    } catch (err) {
      alert(err.message || 'Silme işlemi başarısız');
    }
  };

  const handleRemind = async (lic) => {
    setRemindLoading(lic.id);
    try {
      await generalApi.remindLicense(lic.id);
      loadData();
    } catch (err) {
      alert(err.message || 'Hatırlatıcı gönderilemedi');
    } finally {
      setRemindLoading(null);
    }
  };

  const columns = [
    {
      header: 'Lisans Adı',
      accessorKey: 'name',
      size: 200,
      cell: ({ row }) => {
        const lic = row.original;
        const isHighlight = String(highlightId) === String(lic.id);
        return (
          <div className={`flex flex-col ${isHighlight ? 'ring-2 ring-highlight-500 rounded px-1 py-0.5' : ''}`}>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{lic.name}</span>
            {lic.vendor && <span className="text-xs text-gray-500 dark:text-gray-400">{lic.vendor}</span>}
          </div>
        );
      },
    },
    {
      header: 'Tip',
      accessorKey: 'license_type_name',
      size: 120,
    },
    {
      header: 'Otel',
      accessorKey: 'hotel_name',
      size: 120,
      cell: ({ getValue }) => getValue() || 'Global',
    },
    {
      header: 'Departman',
      accessorKey: 'department_name',
      size: 120,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      header: 'Bitiş Tarihi',
      accessorKey: 'expiry_date',
      size: 110,
    },
    {
      header: 'Durum',
      accessorKey: 'status',
      size: 100,
      cell: ({ getValue }) => {
        const s = getValue();
        const colors = {
          active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
          pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
          cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        };
        return (
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[s] || ''}`}>
            {STATUS_LABELS[s] || s}
          </span>
        );
      },
    },
  ];

  if (loading) return <Loading fullScreen={false} message="Yükleniyor..." />;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lisans Listesi</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Tüm lisansları görüntüle ve yönet</p>
        </div>

        <ActionToolbar className="mb-4">
          <IconActionButton icon={PlusIcon} onClick={openCreate} title="Yeni Lisans" />
          <IconActionButton
            icon={BellAlertIcon}
            onClick={() => selectedLicense && handleRemind(selectedLicense)}
            title="Hatırlat"
            disabled={!selectedLicense || remindLoading === selectedLicense?.id}
          />
          <IconActionButton
            icon={PencilSquareIcon}
            onClick={() => selectedLicense && openEdit(selectedLicense)}
            title="Düzenle"
            disabled={!selectedLicense}
          />
          <IconActionButton
            icon={TrashIcon}
            onClick={() => selectedLicense && handleDelete(selectedLicense)}
            title="Sil"
            disabled={!selectedLicense}
          />
        </ActionToolbar>

        <FilterPanel title="Filtreler" isOpen={filterOpen} onToggle={() => setFilterOpen(!filterOpen)}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label>Ara (Ad, Satıcı, Ürün)</Label>
              <Input
                type="text"
                value={filters.search || ''}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value || undefined }))}
                placeholder="Lisans adı, satıcı veya ürün adı..."
                className="w-full h-10"
              />
            </div>
            <div>
              <Label>Otel</Label>
              <select
                value={filters.hotel || ''}
                onChange={(e) => setFilters((p) => ({ ...p, hotel: e.target.value || undefined }))}
                className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                {hotels.map((h) => (
                  <option key={h.id} value={h.id}>{h.hotel_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Lisans Tipi</Label>
              <select
                value={filters.license_type || ''}
                onChange={(e) => setFilters((p) => ({ ...p, license_type: e.target.value || undefined }))}
                className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                {licenseTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Departman</Label>
              <select
                value={filters.department || ''}
                onChange={(e) => setFilters((p) => ({ ...p, department: e.target.value || undefined }))}
                className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.department_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Durum</Label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value || undefined }))}
                className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-white"
              >
                <option value="">Tümü</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => setFilters({})}>
                Temizle
              </Button>
            </div>
          </div>
        </FilterPanel>

        <DataTable
          data={licenses}
          columns={columns}
          showSearch={false}
          onRowClick={(lic) => setSelectedLicense(lic)}
          onRowDoubleClick={(lic) => openEdit(lic)}
          selectedRowId={selectedLicense?.id}
        />

        <ResponsiveModal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingLicense ? 'Lisans Düzenle' : 'Yeni Lisans'}
            </h2>
            <button type="button" onClick={() => setModalOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSave} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Lisans Adı *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  required
                  placeholder="Örn: Microsoft 365"
                />
              </div>
              <div>
                <Label>Lisans Tipi</Label>
                <select
                  value={formData.license_type || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, license_type: e.target.value || null }))}
                  className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                >
                  <option value="">Seçin</option>
                  {licenseTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Otel</Label>
                <select
                  value={formData.hotel || ''}
                  onChange={(e) => {
                    const hotelId = e.target.value || null;
                    setFormData((p) => ({ ...p, hotel: hotelId, department: '' }));
                  }}
                  className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                >
                  <option value="">Global</option>
                  {hotels.map((h) => (
                    <option key={h.id} value={h.id}>{h.hotel_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Departman</Label>
                <select
                  value={formData.department || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, department: e.target.value || null }))}
                  className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                >
                  <option value="">Seçin</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.department_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Satıcı</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData((p) => ({ ...p, vendor: e.target.value }))}
                  placeholder="Örn: Microsoft"
                />
              </div>
              <div>
                <Label>Ürün Adı</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData((p) => ({ ...p, product_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData((p) => ({ ...p, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData((p) => ({ ...p, expiry_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Yenileme Tarihi</Label>
                <Input
                  type="date"
                  value={formData.renewal_date}
                  onChange={(e) => setFormData((p) => ({ ...p, renewal_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Maliyet</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData((p) => ({ ...p, cost: e.target.value }))}
                />
              </div>
              <div>
                <Label>Para Birimi</Label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData((p) => ({ ...p, currency: e.target.value }))}
                  className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <Label>Durum</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                  className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                >
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Lisans Anahtarı</Label>
                <Input
                  type="password"
                  value={formData.license_key}
                  onChange={(e) => setFormData((p) => ({ ...p, license_key: e.target.value }))}
                  placeholder="Güvenlik için gizli tutulur"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Notlar</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saveLoading}>
                {saveLoading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </ResponsiveModal>
      </div>
    </div>
  );
};

export default LicenseListPage;
